import { io } from 'socket.io-client';
import { getCurrentProject } from './sessionManager';
import { getAudioGraphInstance } from './audioEngine';
import store from './store';
import throttle from 'lodash.throttle'
import debounce from 'lodash.debounce'
import { midiHandler } from './MidiHandler';

const globalMessageThrottleTime = 150; // 150ms

// Initialize socket
const wsServerURL = ''  // Nothing for same domain where the page is loaded
const socket = io(wsServerURL, {
    autoConnect: false,
    reconnection: true,
    path: appPrefix + '/socket.io'
});

socket.on('connect', function() {
    console.debug('Connected to WS server! ID: ' + socket.id)
});

socket.on('disconnect', function(reason) {
    console.debug('Disconnected from WS server! Reason: ' + reason)
});


export const getSocketID = () => {
    if (getCurrentProject().localMode) return "local";
    return socket.id 
}

const throttledFunctionsMap = {}

const getThrottledFuctionKey = (name, data) => {
    if (name !== 'project_action'){
        // Never throttle step sequencer clock messages, midi messages, etc. Only project actions
        return undefined;
    }
    const sliceAction = data.type.replace("project/", "")
    if (!sliceAction.toLowerCase().includes("parameter")) {
        return undefined;
    }
    // sometimes the parameter identifiers are with 'parameter' and other times with 'name'.
    // todo: consolidate with parameterName
    let key = sliceAction;
    key += data.payload.name + "_"
    key += data.payload.parameter + "_"
    return key
}

// Methods to send a message to the server
export const sendMessageToServer = (name, data) => {
    data.project_id = getCurrentProject().getID();

    // for messages that are not project changes (and thus not provoked by the middleware in the store)
    // where things already updated, we want to simulate that we got the message from the server, both if the project is local and
    // if the project is remote
    data.origin_socket_id = null;
    if (name !== "project_action") onMessageFromServer(name, data);
    if (data.isLocal) return;

    if (getCurrentProject().localMode) return;
    // and now we actually send to the server
    data.origin_socket_id = getSocketID();
    const throttledFunctionKey = getThrottledFuctionKey(name, data);
    if (throttledFunctionKey === undefined){
        // No throttle
        socket.emit(name, data);
    } else {
        if (!throttledFunctionsMap.hasOwnProperty(throttledFunctionKey)){
            // If no throttled function exists, create it
            throttledFunctionsMap[throttledFunctionKey] = throttle((name, data) => socket.emit(name, data), globalMessageThrottleTime);
        }
        // Call the throttled version of the funcion
        throttledFunctionsMap[throttledFunctionKey](name, data)
    }
}

export const sendProjectActionToServer = (action) => sendMessageToServer("project_action", action);

// Method used to connect to a project in the server and receive the initial project data
export const joinProjectInServer = (projectID, callback) => {
    socket.connect();
    socket.on('connect', function() {
        console.debug('Joining project', projectID)
        socket.emit('join_project', {project_id: projectID})
        // This will trigger set_project_data from the server
    });
    socket.on('set_project_data', function (data) {
        receivedUpdatesCounter = 0
        callback(data);
    });
}

const requestProjectData = (projectID) => {
    // This will make the server send a "set_project_data" message, therefore "joinProjectInServer" 
    // should have been called before so there is a handler
    console.debug('Requesting project data...')
    socket.emit('request_project_data', {project_id: projectID})
}

// Version of requestProjectData which is not going to be called more than once every 5 seconds
const debouncedRequestProjectData = debounce(projectID => requestProjectData(projectID), 5000, {leading:true, trailing: false});

// Method to request a list of available projects ans subscribe to server updated from this list
export const subscribeToAvailableProjects = (callback) => {
    socket.connect();
    socket.on('connect', function() {
        socket.emit('subscribe_to_available_projects', {})
    });
    socket.on('set_available_projects', function (data) {
        callback(data);
    });
}

// Event handlers for messages received from the server
let receivedUpdatesCounter = 0

// todo: this export is only temporal
export const onMessageFromServer = (name, data) => {
    if (data.hasOwnProperty('update_count')) {
        // If the message is numerated, check that the update number is the expected one
        if (receivedUpdatesCounter === 0){
            receivedUpdatesCounter = data.update_count
        } else {
            if (receivedUpdatesCounter + 1 != data.update_count){
                // We received an update with an unexpected count, state might be
                // out of sync. Request a full project update.
                console.debug('Received update with unexpected count!')
                debouncedRequestProjectData(getCurrentProject().getID());
            }
            receivedUpdatesCounter = data.update_count
        }
    }

    const didThisSendIt = data.origin_socket_id === getSocketID();
    if (name === 'message'){
        console.debug(data, {didThisSendIt});
    } else if (name === 'update_master_sequencer_current_step'){
        getAudioGraphInstance().receiveRemoteTransportCurrentStep(data.current_step);
    }
    if (didThisSendIt) return; // we don't want to react to changes that we already sent
    // except for receiveRemoteTransportCurrentStep apparently?


    if (name === 'project_action') {
        store.dispatch({...data, isLocal: true})
    }
    
    if (name === 'midi_event'){
        midiHandler.receiveMidiEventFromServer(data.instrument_id, data.midi_event_data);
    }
}

socket.on('message', function (message) {
    onMessageFromServer('message', message);
});

socket.on('project_action', function (data) {
    onMessageFromServer('project_action', data);
});

socket.on('midi_event', function (data) {
    onMessageFromServer('midi_event', data);
});
